// Food items for each animal type and their seed requirements
export const foodItems = {
  birds: {
    name: 'Birds',
    food: [
      {
        name: 'Basic Bird Feed',
        rarity: 'Common',
        seeds: [
          { name: 'corn', amount: 1, rarity: 'Common' },
          { name: 'peas', amount: 1, rarity: 'Common' }
        ]
      },
      {
        name: 'Nutritious Mix',
        rarity: 'Uncommon',
        seeds: [
          { name: 'corn', amount: 1, rarity: 'Uncommon' },
          { name: 'peas', amount: 1, rarity: 'Uncommon' }
        ]
      },
      {
        name: 'Premium Blend',
        rarity: 'Rare',
        seeds: [
          { name: 'corn', amount: 1, rarity: 'Rare' },
          { name: 'peas', amount: 1, rarity: 'Rare' }
        ]
      },
      {
        name: 'Golden Seed Mix',
        rarity: 'Epic',
        seeds: [
          { name: 'corn', amount: 1, rarity: 'Epic' },
          { name: 'peas', amount: 1, rarity: 'Epic' }
        ]
      },
      {
        name: 'Divine Avian Banquet',
        rarity: 'Legendary',
        seeds: [
          { name: 'corn', amount: 1, rarity: 'Legendary' },
          { name: 'peas', amount: 1, rarity: 'Legendary' }
        ]
      }
    ]
  },
  cattle: {
    name: 'Cattle',
    icon: '🐄',
    food: [
      {
        name: 'Basic Cattle Feed',
        rarity: 'Common',
        seeds: [
          { name: 'corn', amount: 4, rarity: 'Common' },
          { name: 'peas', amount: 4, rarity: 'Common' }
        ]
      },
      {
        name: 'Nutrient-Rich Mix',
        rarity: 'Uncommon',
        seeds: [
          { name: 'corn', amount: 4, rarity: 'Uncommon' },
          { name: 'peas', amount: 4, rarity: 'Uncommon' }
        ]
      },
      {
        name: 'Premium Cattle Formula',
        rarity: 'Rare',
        seeds: [
          { name: 'corn', amount: 4, rarity: 'Rare' },
          { name: 'peas', amount: 4, rarity: 'Rare' }
        ]
      },
      {
        name: 'Lush Pasture Mix',
        rarity: 'Epic',
        seeds: [
          { name: 'corn', amount: 4, rarity: 'Epic' },
          { name: 'peas', amount: 4, rarity: 'Epic' }
        ]
      },
      {
        name: 'Celestial Bovine Feast',
        rarity: 'Legendary',
        seeds: [
          { name: 'corn', amount: 4, rarity: 'Legendary' },
          { name: 'peas', amount: 4, rarity: 'Legendary' }
        ]
      }
    ]
  },
  fuzzies: {
    name: 'Fuzzies',
    icon: '🐑',
    food: [
      {
        name: 'Basic Fuzzy Feed',
        rarity: 'Common',
        seeds: [
          { name: 'Cabbage', amount: 4, rarity: 'Common' },
          { name: 'Radish', amount: 4, rarity: 'Common' }
        ]
      },
      {
        name: 'Fuzzy Delight',
        rarity: 'Uncommon',
        seeds: [
          { name: 'Cabbage', amount: 4, rarity: 'Uncommon' },
          { name: 'Radish', amount: 4, rarity: 'Uncommon' }
        ]
      },
      {
        name: 'Luxury Fuzzy Feast',
        rarity: 'Rare',
        seeds: [
          { name: 'Cabbage', amount: 4, rarity: 'Rare' },
          { name: 'Radish', amount: 4, rarity: 'Rare' }
        ]
      },
      {
        name: 'Plush Cloud Mix',
        rarity: 'Epic',
        seeds: [
          { name: 'Cabbage', amount: 4, rarity: 'Epic' },
          { name: 'Radish', amount: 4, rarity: 'Epic' }
        ]
      },
      {
        name: 'Divine Fleece Formula',
        rarity: 'Legendary',
        seeds: [
          { name: 'Cabbage', amount: 4, rarity: 'Legendary' },
          { name: 'Radish', amount: 4, rarity: 'Legendary' }
        ]
      }
    ]
  },
  piggies: {
    name: 'Piggies',
    icon: '🐷',
    food: [
      {
        name: 'Truffle Delight',
        rarity: 'Common',
        seeds: [
          { name: 'Milk', amount: 1, rarity: 'Legendary' },
          { name: 'Mangold', amount: 1, rarity: 'Legendary' }
        ],
        output: 1,
        // Indicates this is the amount needed per piggy per day
        dailyPerPiggy: true
      }
    ]
  }
};

export const rarities = [
  { name: 'Common', color: '#9e9e9e' },
  { name: 'Uncommon', color: '#4caf50' },
  { name: 'Rare', color: '#2196f3' },
  { name: 'Epic', color: '#9c27b0' },
  { name: 'Legendary', color: '#ff9800' }
];

// Get animal data by type
export const getAnimalData = (animalType) => {
  return {
    name: foodItems[animalType]?.name || animalType,
    icon: foodItems[animalType]?.icon || '🐾'
  };
};

// Get food image path
export const getFoodImagePath = (animalType, rarity, seedName = '') => {
  // Handle specific seed images
  const seedNameLower = seedName ? seedName.toLowerCase() : '';
  const seedImageMap = {
    'milk': 'milk',
    'mangold': 'mangold',
    'corn': 'corn',
    'peas': 'peas',
    'cabbage': 'cabbage',
    'radish': 'radish'
  };
  
  if (seedNameLower in seedImageMap) {
    // Fuzzies seeds use a different path structure
    if (animalType === 'fuzzies') {
      return `/images/vegetables/${seedNameLower}/${rarity.toLowerCase()}/preview.png`;
    }
    return `/images/vegetables/${seedImageMap[seedNameLower]}/${rarity.toLowerCase()}/preview.png`;
  }
  
  // Map animal types to their corresponding food images
  const foodMap = {
    birds: 'egg',
    cattle: 'milk',
    fuzzies: 'fuzzies',
    piggies: 'truffle'
  };
  
  const foodItem = foodMap[animalType] || 'egg';
  
  // Handle main food image (not seed ingredients)
  if (!seedName) {
    // For Fuzzies, use the seeds/fuzzies directory for the main food image
    if (animalType === 'fuzzies') {
      return `/images/seeds/fuzzies/${rarity.toLowerCase()}/preview.png`;
    }
    // Default path for other animals
    return `/images/seeds/${foodItem}/${rarity.toLowerCase()}/preview.png`;
  }
  
  // Handle seed ingredients
  return `/images/vegetables/${seedName.toLowerCase()}/${rarity.toLowerCase()}/preview.png`;
};

// Get sorted food items for an animal type
export const getSortedFood = (animalType) => {
  const animalFood = foodItems[animalType]?.food || [];
  const rarityOrder = rarities.map(r => r.name);
  
  return [...animalFood].sort((a, b) => {
    return rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity);
  });
};
